import { faceSeconds, showTurnSeconds, turnSeconds, walkSeconds } from './model-motion.js';
import { MODEL_CIRCLE, circleEnd, circleLength, circleOf } from './model-path.js';
/**
 * Realistic 3D models from the platform's library in a lesson (ADR-043;
 * docs/superpowers/specs/2026-10-05-model-library-and-composer-design.md):
 * the names, limits and facts every part shares -- the validator, the
 * headset's engine, the API that serves the facts and the generator that
 * writes lessons with them. Contract C1 of
 * docs/superpowers/plans/2026-10-05-model-library-0-shared.md.
 *
 * A lesson names a model only by `kind: "model:<slug>"`; what the model is
 * (its size, its animations, its sounds) comes from the library at serve
 * time as a `ModelCatalogue`, so a model processed again reaches every lesson.
 */
/**
 * The animations a model can have, in Dutch, as a lesson names them. A new
 * name is a one-line change here and in the schema's enum (a test holds them
 * together), never a change to the headset. `staan` is the idle every
 * animated model has and the engine falls back to.
 */
export const MODEL_ANIMATIONS = [
    'staan',
    'lopen',
    'rennen',
    'brullen',
    'bijten',
    'eten',
    'kijken',
    'liggen',
    'zwemmen',
    'vliegen',
    'springen',
    'aanvallen',
    'slapen',
    // ADR-054: wheels turning, a loop. A car without a wheel clip stays a static exhibit.
    'rijden',
    // ADR-068: a machine at work (a crane lifting, an arm drilling, a press, a mixer): on the spot, once by
    // default, with a call. Appended, so every earlier name keeps its place in this order.
    'werken',
];
/**
 * The animations with a ground speed (ADR-054): what `walkTo`'s `gait` and a `circle`'s `play` may be.
 * Each plays in place while the engine moves the model, at the clip's own speed, so nothing slides.
 */
export const MODEL_GAITS = ['lopen', 'rennen', 'zwemmen', 'vliegen', 'rijden'];
/**
 * The gaits on feet: the media worker measures their speed from foot contacts, they always have one,
 * and they carry the step sounds. A fin, a wing or a wheel has no contacts: the platform admin enters
 * its natural speed (ADR-054, decision 4).
 */
export const MODEL_FOOT_GAITS = ['lopen', 'rennen'];
/** The loops a model may be raised above the land for (`above`): swimmers and flyers. */
export const MODEL_HEIGHT_LOOPS = ['zwemmen', 'vliegen'];
/** The travelling loops without feet: what Les maken names "kan rondjes zwemmen, vliegen, rijden". */
export const MODEL_ROUND_LOOPS = [
    'zwemmen',
    'vliegen',
    'rijden',
];
/**
 * A clip travels -- its root motion carries the model off, like the shark's `circling`, 8.4 m past its
 * box -- when what it sweeps reaches past the model's box, along x or z, by more than `metres` or
 * `share` of the box's longer ground side, whichever is more (ADR-054, refined 10 Oct 2026: a big
 * animal sways a few metres in a bite or a run and does not travel). Such a clip is no loop to move with.
 */
export const MODEL_TRAVEL = { metres: 1, share: 0.5 };
/** The idle: required when a model has animations; a once-only play ends in it by default. */
export const MODEL_IDLE = 'staan';
/**
 * The animations that play in a loop unless a clip says otherwise: the model kit's `model.clip` and the
 * extension's `animaties_benoemen` default (tools/wereld-kit test/model/regels.test.ts holds the kit's
 * copy to this). The rest play once and end in `staan`.
 */
export const MODEL_LOOPING = [
    'staan',
    'lopen',
    'rennen',
    'zwemmen',
    'vliegen',
    'rijden',
    'liggen',
    'slapen',
    'eten',
];
/**
 * What each animation name means, as a maker reads it on "Regels en grenzen" (ADR-072): Dutch, and the
 * English word the console's model words use (`modelWords['en'].animationNames`).
 */
export const MODEL_ANIMATION_MEANINGS = {
    staan: {
        nl: 'De rusthouding: elk model dat beweegt heeft hem, en een animatie die één keer speelt eindigt erin. Een voertuig of machine staat er niet helemaal stil in (een draaiende radar, een zweving).',
        en: 'stand',
    },
    lopen: {
        nl: 'Lopen op de plek; de les verplaatst het model, met de snelheid die het platform aan de voeten meet.',
        en: 'walk',
    },
    rennen: {
        nl: 'Rennen op de plek, sneller dan lopen; de snelheid wordt aan de voeten gemeten.',
        en: 'run',
    },
    brullen: { nl: 'Een roep: brullen, blazen, toeteren.', en: 'roar' },
    bijten: { nl: 'Happen of bijten.', en: 'bite' },
    eten: { nl: 'Eten, grazen of drinken.', en: 'eat' },
    kijken: { nl: 'Rondkijken: de kop of een sensor draait.', en: 'look' },
    liggen: { nl: 'Liggen of rusten.', en: 'lie down' },
    zwemmen: {
        nl: 'Zwemmen op de plek; de les verplaatst het model, ook boven de bodem en in rondjes.',
        en: 'swim',
    },
    vliegen: {
        nl: 'Vliegen op de plek; de les verplaatst het model, ook hoog boven de grond en in rondjes.',
        en: 'fly',
    },
    springen: { nl: 'Een sprong.', en: 'jump' },
    aanvallen: { nl: 'Een aanval, bijvoorbeeld met de staart of de klauwen.', en: 'attack' },
    slapen: { nl: 'Slapen.', en: 'sleep' },
    rijden: {
        nl: 'Wielen die draaien terwijl de les het voertuig verplaatst; een auto zonder deze clip blijft een stilstaand voorwerp.',
        en: 'drive',
    },
    werken: {
        nl: 'Een machine aan het werk (een kraan die tilt, een arm die boort, een pers, een menger), op de plek.',
        en: 'working',
    },
};
/**
 * The model kit's WOW warnings (ADR-065, ADR-068): its taste, never a refusal. The kit's copy
 * (tools/wereld-kit src/model/check.ts `WOW`) is held to this by test/model/regels.test.ts.
 */
export const MODEL_KIT_WOW = {
    /** Kaal van dichtbij: fewer triangles than this per m² of the surface a pupil sees at 7 m... */
    trianglesPerSquareMetre: 15,
    /** ...counting at most this much surface. */
    surfaceSeenAt7m: 100,
    /** Kaal van dichtbij: one flat colour without a texture over more than this, m². */
    flatSquareMetres: 4,
    /** Silhouet: two of the three outlines fill more than this share of their box. */
    silhouetteFill: 0.85,
    /** Budget over: triangles or texture memory cost more than this of a lesson's 100 (draw calls not). */
    cost: 40,
};
export const MODEL_CATEGORIES = ['dieren', 'mens', 'ruimte', 'aarde', 'dingen'];
/**
 * A category as the language models read it. `mens` alone is longer: a cell or a bacterium filed under
 * "mens" read as a person (ADR-093). The stored value never changes; only prompt texts use this.
 */
export function modelCategoryLabel(category) {
    return category === 'mens' ? 'mens en lichaam' : category;
}
export const MODEL_LICENCES = ['cc0', 'cc-by-4.0', 'cc-by-3.0', 'public-domain'];
/** How a licence is written in a credit line (Dutch). */
export const MODEL_LICENCE_LABELS = {
    cc0: 'CC0',
    'cc-by-4.0': 'CC BY 4.0',
    'cc-by-3.0': 'CC BY 3.0',
    'public-domain': 'publiek domein',
};
/** Which of its sides a model's real size is measured along. */
export const MODEL_SIZE_AXES = ['length', 'height'];
/**
 * A model sound's part: `step` at the foot contacts of a gait, `call` at a
 * moment into another animation (a roar, a bite), `loop` the breath while it
 * stands.
 */
export const MODEL_SOUND_ROLES = ['step', 'call', 'loop'];
/** A library model's slug: what `model:<slug>` names. Never reused. */
export const MODEL_SLUG = /^[a-z0-9-]{2,40}$/;
export const MODEL_KIND_PREFIX = 'model:';
/** The object fields only a model takes. */
export const MODEL_OBJECT_FIELDS = ['turn', 'size', 'above'];
/** The step fields that are about models. */
export const MODEL_STEP_FIELDS = ['models', 'spannend'];
/** The library's and a lesson's limits (spec, "Decided for you" 3 and 4). */
export const MODEL_LIMITS = {
    /** Model objects in one lesson, and entries in one step's `models`. */
    perLesson: 5,
    /** The summed `cost` of a lesson's models: the hidden Quest budget. */
    costPerLesson: 100,
    /** One walk, seconds. */
    walkSeconds: 12,
    /** A lesson's `size` as a share of the real size. */
    sizeFactorMin: 0.1,
    sizeFactorMax: 2,
    /** A model's real size along its size axis, metres. */
    realSizeMin: 0.05,
    realSizeMax: 40,
    /** Over this many triangles a model is simplified to it. */
    trianglesMax: 40_000,
    /** Still over this after simplifying: `too-heavy`. */
    trianglesHardMax: 44_000,
    /**
     * ADR-068 (was 12): a machine of many moving parts. The lesson budget (`costPerLesson` against
     * `MODEL_COST.drawCalls` 30) is unchanged, so a 20-draw-call model costs 67 of a lesson's 100.
     */
    drawCallsMax: 20,
    gpuBytesMax: 24 * 2 ** 20,
    /** The served GLB. */
    fileBytesMax: 12 * 2 ** 20,
    /** An upload, GLB or FBX. */
    uploadBytesMax: 100 * 2 ** 20,
    texturesMax: 8,
    /** Every texture's longest side, pixels. */
    textureSize: 512,
    /** One sound take: Ogg Opus, mono. */
    soundBytesMax: 2 ** 20,
    soundSecondsMax: 15,
    /** Takes per sound, played in turn. */
    takesMax: 4,
    gainDbMin: -12,
    gainDbMax: 3,
    nameMax: 40,
    /** A model's English name (the Dutch one is `nameMax`), for the console in English and search. */
    nameEnMax: 80,
    wordsMax: 20,
    wordMax: 40,
    titleMax: 120,
    authorMax: 80,
    sourceUrlMax: 300,
    creditMax: 200,
    /** The brief's "Wat gebeurt er", code points. */
    storyMax: 1_000,
    requestNameMax: 60,
    requestLineMax: 200,
    requestNoteMax: 300,
    openRequestsPerSchool: 10,
    /** A platform 360° photo's name as a place card. */
    placeNameMax: 40,
};
/**
 * The distance rule's numbers (spec, "The distance rule"), metres. The
 * rule itself is Track A's (model-distance.ts).
 */
export const MODEL_DISTANCE = {
    /**
     * Calm: and at least this far from the pupil's eyes (ADR-084: 3 m since 29 Oct 2026, was 6; `STAGE.modelEyes`,
     * ADR-032's frozen stage keeps 6).
     */
    calmEyes: 3,
    /** Spannend: its front never nearer than this to the eyes; the engine clamps a walk there. */
    spannendEyes: 2.5,
    /** A small model, calm or Spannend. */
    smallEyes: 1.5,
    /** Small: a box at most this high and at most this long. */
    smallHeight: 2.5,
    smallLength: 3,
    /** Added round every footprint. */
    margin: 0.3,
    /** A walk is checked every this many metres. */
    sample: 0.25,
};
/** What a share of 100 of the lesson budget is (spec, "Decided for you" 4). */
export const MODEL_COST = {
    triangles: 120_000,
    gpuBytes: 32 * 2 ** 20,
    drawCalls: 30,
};
/** Whether `kind` places a library model: `model:<slug>`. */
export function isModelKind(kind) {
    return kind.startsWith(MODEL_KIND_PREFIX);
}
/** The slug `kind` names, or undefined when it is not a model. */
export function modelSlugOf(kind) {
    return isModelKind(kind) ? kind.slice(MODEL_KIND_PREFIX.length) : undefined;
}
/**
 * A model's share of the lesson budget: its heaviest of triangles, GPU
 * texture memory and draw calls against what a lesson's models may use
 * together, as a whole percentage, rounded up, 1 to 100. The spike's T. rex
 * is 10. To be calibrated on a Quest 3 (spec, "To test on device" 3).
 */
export function modelCost(model) {
    const share = Math.max(model.triangles / MODEL_COST.triangles, model.gpuBytes / MODEL_COST.gpuBytes, model.drawCalls / MODEL_COST.drawCalls);
    // A whisker of float error must not push 10.000000001 to 11.
    const cost = Math.ceil(Math.round(share * 100 * 1e6) / 1e6);
    return Math.min(100, Math.max(1, cost));
}
/**
 * The credit line shown wherever a model is (spec, "Privacy and licensing"):
 * `"{title}" door {author}, {licence}, aangepast` -- we always adapt a model
 * -- and without "aangepast" for CC0 and the public domain, which ask for no
 * attribution at all.
 */
export function modelCredit(model) {
    const free = model.licence === 'cc0' || model.licence === 'public-domain';
    const label = MODEL_LICENCE_LABELS[model.licence];
    return `"${model.title}" door ${model.author}, ${label}${free ? '' : ', aangepast'}`;
}
/** Whether a sound of `role` belongs on `animation`: steps on a foot gait, the breath on staan, calls on the rest. */
export function soundRoleFits(role, animation) {
    const feet = MODEL_FOOT_GAITS.includes(animation);
    switch (role) {
        case 'step':
            return feet;
        case 'loop':
            return animation === MODEL_IDLE;
        case 'call':
            return !feet && animation !== MODEL_IDLE;
    }
}
/** How much bigger the lesson shows a model than real: its `size` over its real size, or 1. */
export function modelScale(object, facts) {
    return object.size === undefined ? 1 : object.size / facts.realSize;
}
/**
 * Where a model with `turn` (degrees) faces, as [x, z] on the ground: the
 * rig's yaw convention -- 0 faces −z, a positive turn turns left
 * (anticlockwise seen from above, three.js `rotation.y`).
 */
export function modelForward(turn) {
    const radians = (turn * Math.PI) / 180;
    return [-Math.sin(radians), -Math.cos(radians)];
}
/** The turn, degrees, that faces a model standing at `from` towards `to` ([x, z]). */
export function turnTowards(from, to) {
    return (Math.atan2(-(to[0] - from[0]), -(to[1] - from[1])) * 180) / Math.PI;
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isVec3 = (value) => Array.isArray(value) && value.length === 3 && value.every(isFiniteNumber);
/** 12.1 as "12,1" and 14 as "14". */
export const dutchNumber = (n) => String(Math.round(n * 10) / 10).replace('.', ',');
const isAnimation = (value) => typeof value === 'string' && MODEL_ANIMATIONS.includes(value);
const isGait = (value) => typeof value === 'string' && MODEL_GAITS.includes(value);
/**
 * The gait a walk (`walkTo`) goes in: the entry's `gait` (default `lopen`) when the model has a speed
 * for it; otherwise its own way of getting about -- `rijden` for a vehicle, else `lopen`, else any
 * gait it has a speed for -- so a walk in a gait it lacks still takes it there rather than leaving it
 * standing (the marsrover lesson, 15 Oct 2026: "gait": "rennen" for a rover that only drives). The
 * validator still refuses such an entry ("kan niet rennen"); the replay and the headset go by this.
 * With no facts, or no gait with a speed, the asked gait.
 */
export function walkingGait(facts, asked) {
    const wanted = isGait(asked) ? asked : 'lopen';
    if (!facts)
        return wanted;
    const moves = (gait) => (facts.animations[gait]?.speed ?? 0) > 0;
    if (moves(wanted))
        return wanted;
    const own = ['rijden', 'lopen'].find(moves) ?? MODEL_GAITS.find(moves);
    return own ?? wanted;
}
const isFootGait = (value) => typeof value === 'string' && MODEL_FOOT_GAITS.includes(value);
/**
 * How far a clip carries the model off, metres, when it travels (`MODEL_TRAVEL`): the largest overshoot
 * of its bounds past the box on the ground plan. Undefined when it stays in place. Box and bounds in
 * the model frame at real size.
 */
export function clipTravel(box, bounds) {
    const over = (lo, hi, side) => Math.max(-side / 2 - lo, hi - side / 2);
    const allowed = Math.max(MODEL_TRAVEL.metres, MODEL_TRAVEL.share * Math.max(box.width, box.length));
    const most = Math.max(over(bounds.min[0], bounds.max[0], box.width), over(bounds.min[2], bounds.max[2], box.length));
    return most <= allowed + 1e-9 ? undefined : most;
}
/**
 * The gaits a model can go round with (ADR-054), in `MODEL_GAITS` order: a loop that stays in place
 * and has a speed. Empty for a static exhibit, and for a car without a wheel clip.
 */
export function circleLoops(facts) {
    return MODEL_GAITS.filter((gait) => {
        const clip = facts.animations[gait];
        return (clip !== undefined &&
            clip.loop &&
            isFiniteNumber(clip.speed) &&
            clip.speed > 0 &&
            clipTravel(facts.box, clip.bounds) === undefined);
    });
}
/** How a model going round is said in Dutch, by its loop: "een model dat rondzwemt". */
export const ROUND_WORDS = {
    lopen: 'rondloopt',
    rennen: 'rondrent',
    zwemmen: 'rondzwemt',
    vliegen: 'rondvliegt',
    rijden: 'rondrijdt',
};
/** A step's subjects: what `view.look`, `focus`, `highlight` and its hotspots name, with their paths. */
function subjectsOf(step, at) {
    const found = [];
    const view = step['view'];
    if (isRecord(view) && typeof view['look'] === 'string') {
        found.push({ id: view['look'], path: `${at}/view/look` });
    }
    if (typeof step['focus'] === 'string')
        found.push({ id: step['focus'], path: `${at}/focus` });
    if (typeof step['highlight'] === 'string') {
        found.push({ id: step['highlight'], path: `${at}/highlight` });
    }
    const hotspots = Array.isArray(step['hotspots']) ? step['hotspots'] : [];
    hotspots.forEach((hotspot, h) => {
        if (isRecord(hotspot) && typeof hotspot['object'] === 'string') {
            found.push({ id: hotspot['object'], path: `${at}/hotspots/${h}/object` });
        }
    });
    return found;
}
/** `walkTo` as a place: two finite numbers. */
export const isWalkPlace = (value) => Array.isArray(value) && value.length === 2 && value.every(isFiniteNumber);
/**
 * Every model rule but distance (model-distance.ts), read defensively: the
 * generator's checks call it on answers the schema may have refused. Without
 * a catalogue only what needs none: where a model may stand, the counts and
 * the shape of `models` and `spannend`. With one, also that the slug and
 * every animation exist, that a gait has a speed, that the models fit the
 * lesson budget and that `size` stays within 0.1–2 × the real size. A walk's
 * 12 s is the distance rule's (model-steps.ts): it depends on where the
 * pupil a face turned to stands.
 */
export function assessModels(document, catalogue) {
    const problems = [];
    if (!isRecord(document))
        return problems;
    const objects = isRecord(document['objects']) ? document['objects'] : {};
    const steps = Array.isArray(document['steps']) ? document['steps'] : [];
    const add = (problem) => void problems.push(problem);
    /** Whether an object moves in the lesson: a `move`, or a model's walk or circle (ADR-054). */
    const movesItself = (target) => steps.some((step) => {
        if (!isRecord(step))
            return false;
        const moved = Array.isArray(step['move']) &&
            step['move'].some((m) => isRecord(m) && m['object'] === target);
        const lists = [step['models'], isRecord(step['spannend']) ? step['spannend']['models'] : []];
        const goes = lists.some((list) => Array.isArray(list) &&
            list.some((e) => isRecord(e) &&
                e['object'] === target &&
                (e['walkTo'] !== undefined || e['circle'] !== undefined)));
        return moved || goes;
    });
    /** What is wrong with going round `target`, if anything (ADR-054, decision 1 and settled 6). */
    const aroundProblem = (target, self) => {
        if (target === self) {
            return {
                en: `"${self}" cannot go round itself`,
                nl: `"${self}" kan niet om zichzelf heen gaan`,
            };
        }
        const object = objects[target];
        if (!isRecord(object)) {
            return {
                en: `circle.around names unknown object "${target}"`,
                nl: `"${target}" bestaat niet`,
            };
        }
        if (!isVec3(object['pos']) || object['parent'] !== undefined || object['orbit'] !== undefined) {
            return {
                en: `"${target}" has no place of its own on the ground to go round`,
                nl: `"${target}" heeft geen eigen plek om omheen te gaan`,
            };
        }
        if (movesItself(target)) {
            return {
                en: `"${target}" moves in the lesson, so it is no centre to go round`,
                nl: `"${target}" beweegt in de les, dus het is geen midden om omheen te gaan`,
            };
        }
        return undefined;
    };
    const modelObject = (id) => {
        const object = objects[id];
        return isRecord(object) && typeof object['kind'] === 'string' && isModelKind(object['kind'])
            ? object
            : undefined;
    };
    const factsOf = (object) => {
        const slug = modelSlugOf(object['kind']);
        return slug === undefined ? undefined : catalogue?.[slug];
    };
    let count = 0;
    let cost = 0;
    for (const [id, object] of Object.entries(objects)) {
        if (!isRecord(object))
            continue;
        const at = `/objects/${id}`;
        const kind = typeof object['kind'] === 'string' ? object['kind'] : '';
        const shape = (path, en, nl) => add({ path, en, nl, object: id, kind: 'shape' });
        if (!isModelKind(kind)) {
            for (const field of MODEL_OBJECT_FIELDS) {
                if (object[field] !== undefined) {
                    shape(`${at}/${field}`, `"${id}" has ${field}, which only a model takes`, `"${id}": ${field} kan alleen op een model`);
                }
            }
            continue;
        }
        count++;
        const slug = modelSlugOf(kind);
        if (!MODEL_SLUG.test(slug)) {
            // The schema lets `model:` through with a digit first (a slug may start
            // with one) and, without lookaheads a grammar engine would refuse, any
            // other word too: the slug's own rule is said here.
            shape(`${at}/kind`, `"${id}" is ${kind}, but a model's slug is 2–40 of a–z, 0–9 and -`, `"${id}": ${kind} is geen modelnaam (2 tot 40 tekens: a–z, 0–9 en -)`);
        }
        const pos = object['pos'];
        if (object['parent'] !== undefined || object['orbit'] !== undefined || !isVec3(pos)) {
            shape(at, `"${id}" is a model, so it stands on the ground by pos, not in an orbit`, `"${id}" is een model: het staat met pos op de grond, niet in een baan`);
        }
        else if (pos[1] !== 0) {
            shape(`${at}/pos`, `"${id}" is a model, which stands on the ground: its pos has y 0`, `"${id}" is een model en staat op de grond: de y van pos is 0`);
        }
        for (const field of ['rot', 'scale']) {
            if (object[field] !== undefined) {
                shape(`${at}/${field}`, `"${id}" is a model, turned by turn and sized by size, not by ${field}`, `"${id}" is een model: draai het met turn en maak het groter met size, niet met ${field}`);
            }
        }
        if (document['mode'] === 'ar') {
            shape(at, `"${id}" is a model, which only a vr lesson allows`, `"${id}" is een model, en modellen werken alleen in VR`);
        }
        if (catalogue === undefined)
            continue;
        const facts = factsOf(object);
        if (!facts) {
            add({
                path: `${at}/kind`,
                en: `"${id}" is ${kind}, which is not in the model library`,
                nl: `"${id}": ${kind} staat niet in de modellenbibliotheek`,
                object: id,
                kind: 'unknown-model',
            });
            continue;
        }
        cost += facts.cost;
        const size = object['size'];
        if (isFiniteNumber(size)) {
            const factor = size / facts.realSize;
            if (factor < MODEL_LIMITS.sizeFactorMin - 1e-9 ||
                factor > MODEL_LIMITS.sizeFactorMax + 1e-9) {
                const low = facts.realSize * MODEL_LIMITS.sizeFactorMin;
                const high = facts.realSize * MODEL_LIMITS.sizeFactorMax;
                add({
                    path: `${at}/size`,
                    en: `"${id}" is ${size} m; ${facts.name} may be ${low}–${high} m (0.1–2 × its real ${facts.realSize} m)`,
                    nl: `"${id}" is ${dutchNumber(size)} m; ${facts.name} mag ${dutchNumber(low)} tot ${dutchNumber(high)} m zijn (0,1 tot 2 × de echte ${dutchNumber(facts.realSize)} m)`,
                    object: id,
                    kind: 'size',
                });
            }
        }
        const above = object['above'];
        if (isFiniteNumber(above) && above > 0) {
            const raised = MODEL_HEIGHT_LOOPS.some((loop) => facts.animations[loop] !== undefined);
            const scale = isFiniteNumber(size) ? size / facts.realSize : 1;
            const small = facts.box.height * scale <= MODEL_DISTANCE.smallHeight + 1e-9 &&
                facts.box.length * scale <= MODEL_DISTANCE.smallLength + 1e-9;
            if (!raised) {
                add({
                    path: `${at}/above`,
                    en: `"${id}" is ${facts.name}, which neither swims nor flies, so it stands on the land (no above)`,
                    nl: `"${id}": ${facts.name} zwemt en vliegt niet, dus het staat op de grond (geen above)`,
                    object: id,
                    kind: 'height',
                });
            }
            else if (small) {
                // Decision 3: no small flyers over the stage in v1.
                add({
                    path: `${at}/above`,
                    en: `"${id}" is a small model; a small one does not swim or fly above the land yet`,
                    nl: `"${id}" is een klein model; een klein model zweeft nog niet boven de grond`,
                    object: id,
                    kind: 'height',
                });
            }
        }
    }
    if (count > MODEL_LIMITS.perLesson) {
        add({
            path: '/objects',
            en: `${count} models; a lesson may have at most ${MODEL_LIMITS.perLesson}`,
            nl: `${count} modellen (hooguit ${MODEL_LIMITS.perLesson})`,
            kind: 'too-many-models',
        });
    }
    if (catalogue !== undefined && cost > MODEL_LIMITS.costPerLesson) {
        add({
            path: '/objects',
            en: `the models are too heavy for the headset together (${cost} of ${MODEL_LIMITS.costPerLesson})`,
            nl: `de modellen zijn samen te zwaar voor de bril (${cost} van de ${MODEL_LIMITS.costPerLesson})`,
            kind: 'over-budget',
        });
    }
    steps.forEach((step, index) => {
        if (!isRecord(step))
            return;
        const at = `/steps/${index}`;
        const name = typeof step['id'] === 'string' ? step['id'] : String(index + 1);
        const inStep = `stap "${name}": `;
        const moves = Array.isArray(step['move']) ? step['move'] : [];
        moves.forEach((move, m) => {
            const id = isRecord(move) ? move['object'] : undefined;
            if (typeof id !== 'string' || !modelObject(id))
                return;
            add({
                path: `${at}/move/${m}/object`,
                en: `"${id}" is a model, which walks (walkTo) rather than moves`,
                nl: `${inStep}"${id}" is een model en loopt met walkTo; move werkt er niet op`,
                step: name,
                object: id,
                kind: 'shape',
            });
        });
        const entry = (value, path, spannend, seen) => {
            if (!isRecord(value) || typeof value['object'] !== 'string')
                return;
            const id = value['object'];
            const problem = (kind, where, en, nl) => add({ path: where, en, nl: `${inStep}${nl}`, step: name, object: id, kind });
            const object = modelObject(id);
            if (!object) {
                problem('shape', `${path}/object`, objects[id] === undefined
                    ? `models references unknown object "${id}"`
                    : `"${id}" is not a model, so it has no place in models`, objects[id] === undefined ? `"${id}" bestaat niet` : `"${id}" is geen model`);
                return;
            }
            if (seen.has(id)) {
                problem('shape', `${path}/object`, `"${id}" is named twice`, `"${id}" staat er twee keer in`);
                return;
            }
            seen.add(id);
            const walkTo = value['walkTo'];
            const walking = walkTo !== undefined;
            if (walkTo === 'pupil' && !spannend) {
                problem('spannend', `${path}/walkTo`, 'a model walks to the pupil only in a spannend part', `"${id}" loopt alleen in een spannend deel naar de leerling`);
            }
            if (spannend && walking && walkTo !== 'pupil') {
                problem('spannend', `${path}/walkTo`, `in a spannend part "${id}" walks only to the pupil, so the next step starts where the calm part left it`, `"${id}" loopt in een spannend deel alleen naar de leerling, zodat de volgende stap begint waar de rustige versie eindigt`);
            }
            // A turn in place ("Wat wil je maken?" plan 4): its own heading, so never beside another way of
            // turning or moving, and only in the calm part, which the next step starts from.
            if (value['turn'] !== undefined) {
                if (spannend) {
                    problem('spannend', `${path}/turn`, 'a model turns in place only in the calm part', `"${id}" draait alleen in de rustige versie op zijn plek, niet in een spannend deel`);
                }
                for (const field of ['walkTo', 'face']) {
                    if (value[field] !== undefined) {
                        problem('shape', `${path}/turn`, `"${id}" turns in place, so it takes no ${field}`, `"${id}" draait op zijn plek: ${field} kan er niet bij`);
                    }
                }
                if (value['circle'] !== undefined) {
                    problem('circle', `${path}/turn`, `"${id}" goes round, so it takes no turn`, `"${id}" gaat rond: turn kan er niet bij`);
                }
            }
            if (value['gait'] !== undefined && !walking) {
                problem('shape', `${path}/gait`, `gait needs a walkTo`, `gait hoort bij walkTo`);
            }
            if (value['loop'] !== undefined && value['play'] === undefined) {
                problem('shape', `${path}/loop`, `loop needs a play`, `loop hoort bij play`);
            }
            const circle = value['circle'];
            if (circle !== undefined) {
                if (spannend) {
                    problem('spannend', `${path}/circle`, 'a model goes round only in the calm part', `"${id}" gaat alleen in de rustige versie rond, niet in een spannend deel`);
                }
                for (const field of ['walkTo', 'face', 'loop']) {
                    if (value[field] !== undefined) {
                        problem('circle', `${path}/${field}`, `"${id}" goes round, so it takes no ${field}`, `"${id}" gaat rond: ${field} kan er niet bij`);
                    }
                }
                if (!isGait(value['play'])) {
                    problem('circle', `${path}/play`, `going round needs play: one of ${MODEL_GAITS.join(', ')}`, `"${id}" gaat rond: play is dan ${MODEL_GAITS.join(', ')}`);
                }
                const around = isRecord(circle) ? circle['around'] : undefined;
                if (typeof around === 'string') {
                    const why = aroundProblem(around, id);
                    if (why)
                        problem('circle', `${path}/circle/around`, why.en, why.nl);
                }
            }
            if (!walking &&
                value['turn'] === undefined &&
                value['face'] === undefined &&
                value['play'] === undefined &&
                value['then'] === undefined) {
                problem('shape', path, `the entry for "${id}" does nothing: give walkTo, turn, face, play or then`, `"${id}" doet niets (geef walkTo, turn, face, play of then)`);
            }
            const facts = factsOf(object);
            if (!facts)
                return;
            const has = (animation) => facts.animations[animation] !== undefined;
            const cannot = (where, animation) => problem('unknown-animation', where, `${facts.name} ("${id}") cannot ${animation}`, `${facts.name} ("${id}") kan niet ${animation}`);
            if (walking) {
                const gait = isGait(value['gait']) ? value['gait'] : 'lopen';
                const where = value['gait'] === undefined ? `${path}/walkTo` : `${path}/gait`;
                if (!has(gait))
                    cannot(where, gait);
                else if (!isFiniteNumber(facts.animations[gait].speed) ||
                    facts.animations[gait].speed <= 0) {
                    problem('no-speed', where, `${facts.name} has no speed for ${gait}, so "${id}" cannot walk with it`, `${facts.name} heeft geen snelheid voor ${gait}, dus "${id}" kan er niet mee lopen`);
                }
            }
            for (const field of ['play', 'then']) {
                const animation = value[field];
                if (isAnimation(animation) && !has(animation))
                    cannot(`${path}/${field}`, animation);
            }
            const play = value['play'];
            if (circle !== undefined && isGait(play) && has(play)) {
                const clip = facts.animations[play];
                if (!isFiniteNumber(clip.speed) || clip.speed <= 0) {
                    problem('no-speed', `${path}/play`, `${facts.name} has no speed for ${play}, so "${id}" cannot go round with it`, `${facts.name} heeft geen snelheid voor ${play}, dus "${id}" kan er niet mee rondgaan`);
                }
                else {
                    const travels = clipTravel(facts.box, clip.bounds);
                    if (travels !== undefined) {
                        problem('circle', `${path}/play`, `${play} carries ${facts.name} off itself (${travels.toFixed(1)} m), so it is no loop to go round with`, `${play} verplaatst ${facts.name} zelf (${dutchNumber(travels)} m): geen lus om mee rond te gaan`);
                    }
                }
            }
        };
        const calm = Array.isArray(step['models']) ? step['models'] : undefined;
        const calmSeen = new Set();
        calm?.forEach((value, e) => entry(value, `${at}/models/${e}`, false, calmSeen));
        const spannend = step['spannend'];
        if (spannend === undefined)
            return;
        if (calm === undefined || calm.length === 0) {
            add({
                path: `${at}/spannend`,
                en: 'a spannend part needs a calm version: the step’s own models',
                nl: `${inStep}een spannend deel hoort bij een rustige versie (models van de stap)`,
                step: name,
                kind: 'spannend',
            });
            return;
        }
        const list = isRecord(spannend) && Array.isArray(spannend['models']) ? spannend['models'] : [];
        const seen = new Set();
        list.forEach((value, e) => {
            const path = `${at}/spannend/models/${e}`;
            const id = isRecord(value) ? value['object'] : undefined;
            if (typeof id === 'string' && modelObject(id) && !calmSeen.has(id)) {
                add({
                    path: `${path}/object`,
                    en: `the spannend part names "${id}", which the calm part does not`,
                    nl: `${inStep}het spannende deel noemt "${id}", de rustige versie niet`,
                    step: name,
                    object: id,
                    kind: 'spannend',
                });
                return;
            }
            entry(value, path, true, seen);
        });
        // It replaces the calm part, so a model it left out would stand still
        // and then jump at the next step: it names every one the calm part names.
        for (const id of calmSeen) {
            if (seen.has(id))
                continue;
            add({
                path: `${at}/spannend`,
                en: `the spannend part replaces the calm one, so it names "${id}" too`,
                nl: `${inStep}het spannende deel vervangt de rustige versie, dus noemt het ook "${id}"`,
                step: name,
                object: id,
                kind: 'spannend',
            });
        }
    });
    // Who goes round at each step (ADR-054): a circle in the step, or a loop from an earlier one the
    // step does not name. Such a model is never the step's subject; one goes round at a time, and at
    // most two models move at once (walking or going round), calm and Spannend.
    const looping = new Map();
    steps.forEach((step, index) => {
        if (!isRecord(step))
            return;
        const at = `/steps/${index}`;
        const name = typeof step['id'] === 'string' ? step['id'] : String(index + 1);
        const calm = Array.isArray(step['models']) ? step['models'] : [];
        const named = new Set();
        const circling = new Map();
        let walking = 0;
        for (const value of calm) {
            if (!isRecord(value) || typeof value['object'] !== 'string')
                continue;
            const id = value['object'];
            if (!modelObject(id) || named.has(id))
                continue;
            named.add(id);
            if (isRecord(value['circle']))
                circling.set(id, isGait(value['play']) ? value['play'] : 'lopen');
            else if (value['walkTo'] !== undefined)
                walking++;
        }
        for (const [id, gait] of looping)
            if (!named.has(id))
                circling.set(id, gait);
        for (const { id, path } of subjectsOf(step, at)) {
            const gait = circling.get(id);
            if (gait === undefined)
                continue;
            add({
                path,
                en: `"${id}" goes round in step "${name}", so it cannot be the step's subject: stop it first`,
                nl: `stap "${name}": een model dat ${ROUND_WORDS[gait]} ("${id}") kan niet het onderwerp zijn; laat het eerst stoppen`,
                step: name,
                object: id,
                kind: 'subject',
            });
        }
        if (circling.size > MODEL_CIRCLE.circlingMax) {
            add({
                path: `${at}/models`,
                en: `${circling.size} models go round at once; at most ${MODEL_CIRCLE.circlingMax}`,
                nl: `stap "${name}": ${circling.size} modellen gaan tegelijk rond (hooguit ${MODEL_CIRCLE.circlingMax})`,
                step: name,
                kind: 'comfort',
            });
        }
        if (circling.size + walking > MODEL_CIRCLE.movingMax) {
            add({
                path: `${at}/models`,
                en: `${circling.size + walking} models move at once; at most ${MODEL_CIRCLE.movingMax}`,
                nl: `stap "${name}": ${circling.size + walking} modellen bewegen tegelijk (hooguit ${MODEL_CIRCLE.movingMax})`,
                step: name,
                kind: 'comfort',
            });
        }
        const part = isRecord(step['spannend']) ? step['spannend']['models'] : undefined;
        if (Array.isArray(part) && part.length > 0) {
            const inPart = new Set();
            let walkers = 0;
            for (const value of part) {
                if (!isRecord(value) || typeof value['object'] !== 'string')
                    continue;
                inPart.add(value['object']);
                if (value['walkTo'] !== undefined)
                    walkers++;
            }
            const carried = [...looping.keys()].filter((id) => !inPart.has(id)).length;
            if (carried + walkers > MODEL_CIRCLE.movingMax) {
                add({
                    path: `${at}/spannend/models`,
                    en: `${carried + walkers} models move at once in the spannend part; at most ${MODEL_CIRCLE.movingMax}`,
                    nl: `stap "${name}", spannend: ${carried + walkers} modellen bewegen tegelijk (hooguit ${MODEL_CIRCLE.movingMax})`,
                    step: name,
                    kind: 'comfort',
                });
            }
        }
        for (const value of calm) {
            if (!isRecord(value) || typeof value['object'] !== 'string')
                continue;
            const ring = value['circle'];
            if (isRecord(ring) && ring['rounds'] === 'loop' && isGait(value['play'])) {
                looping.set(value['object'], value['play']);
            }
            else {
                looping.delete(value['object']);
            }
        }
    });
    return problems;
}
/**
 * A served lesson's `models`, read leniently as `mediaInfoOf` reads
 * `mediaInfo`: a model whose facts cannot be trusted is dropped (its object
 * then shows nothing and the lesson goes on), as is an animation or a sound
 * that cannot -- a gait without a speed, a sound on an animation the model
 * does not have or in a role that does not fit it. Junk reads as `{}`.
 */
export function modelCatalogueOf(value) {
    const catalogue = {};
    if (!isRecord(value))
        return catalogue;
    for (const [slug, raw] of Object.entries(value)) {
        const facts = factsOf(slug, raw);
        if (facts)
            catalogue[slug] = facts;
    }
    return catalogue;
}
const MEDIA = /^m_[A-Za-z0-9_-]{1,64}$/;
const oneOf = (list, value) => typeof value === 'string' && list.includes(value);
function boxOf(value) {
    if (!isRecord(value) || !isVec3(value['min']) || !isVec3(value['max']))
        return undefined;
    const [min, max] = [value['min'], value['max']];
    if (![0, 1, 2].every((i) => min[i] <= max[i]))
        return undefined;
    return { min: [min[0], min[1], min[2]], max: [max[0], max[1], max[2]] };
}
function clipOf(animation, value) {
    if (!isRecord(value))
        return undefined;
    const { clip, loop, seconds, speed, contacts } = value;
    const bounds = boxOf(value['bounds']);
    if (typeof clip !== 'string' || clip.length === 0 || typeof loop !== 'boolean')
        return undefined;
    if (!isFiniteNumber(seconds) || seconds <= 0 || !bounds)
        return undefined;
    if (!Array.isArray(contacts) || !contacts.every((c) => isFiniteNumber(c) && c >= 0 && c <= 1)) {
        return undefined;
    }
    // A foot gait always has a speed (the worker measures it); a swim, flight or drive keeps the
    // admin's when there is one, and is still played without (it cannot move then).
    if (isFootGait(animation) && (!isFiniteNumber(speed) || speed <= 0))
        return undefined;
    const moves = isGait(animation) && isFiniteNumber(speed) && speed > 0;
    return {
        clip,
        loop,
        seconds,
        ...(moves ? { speed: speed } : {}),
        contacts: [...contacts],
        bounds,
    };
}
function soundOf(value, animations) {
    if (!isRecord(value))
        return undefined;
    const { animation, role, media, at, gainDb, credit } = value;
    if (!isAnimation(animation) || animations[animation] === undefined)
        return undefined;
    if (!oneOf(MODEL_SOUND_ROLES, role) || !soundRoleFits(role, animation))
        return undefined;
    if (!Array.isArray(media) ||
        media.length < 1 ||
        media.length > MODEL_LIMITS.takesMax ||
        !media.every((id) => typeof id === 'string' && MEDIA.test(id))) {
        return undefined;
    }
    if (!isFiniteNumber(gainDb) ||
        gainDb < MODEL_LIMITS.gainDbMin ||
        gainDb > MODEL_LIMITS.gainDbMax) {
        return undefined;
    }
    if (role === 'call' && (!isFiniteNumber(at) || at < 0))
        return undefined;
    return {
        animation,
        role,
        media: [...media],
        ...(role === 'call' ? { at: at } : {}),
        gainDb,
        ...(typeof credit === 'string' && credit !== '' ? { credit } : {}),
    };
}
function factsOf(slug, value) {
    if (!MODEL_SLUG.test(slug) || !isRecord(value) || value['slug'] !== slug)
        return undefined;
    const { name, category, media, sizeAxis, realSize, box, forwardYaw, licence, credit } = value;
    const { triangles, drawCalls, gpuBytes, cost } = value;
    if (typeof name !== 'string' || name.length === 0 || typeof credit !== 'string')
        return undefined;
    if (!oneOf(MODEL_CATEGORIES, category) || !oneOf(MODEL_LICENCES, licence))
        return undefined;
    if (typeof media !== 'string' || !MEDIA.test(media))
        return undefined;
    if (!oneOf(MODEL_SIZE_AXES, sizeAxis) || !isFiniteNumber(realSize) || realSize <= 0) {
        return undefined;
    }
    if (!isRecord(box))
        return undefined;
    const sides = [box['length'], box['height'], box['width']];
    if (!sides.every((side) => isFiniteNumber(side) && side > 0))
        return undefined;
    if (!isFiniteNumber(forwardYaw))
        return undefined;
    if (![triangles, drawCalls, gpuBytes].every((n) => isFiniteNumber(n) && n >= 0))
        return undefined;
    if (!isFiniteNumber(cost) || cost < 1 || cost > 100)
        return undefined;
    const animations = {};
    if (isRecord(value['animations'])) {
        for (const [animation, raw] of Object.entries(value['animations'])) {
            if (!isAnimation(animation))
                continue;
            const clip = clipOf(animation, raw);
            if (clip)
                animations[animation] = clip;
        }
    }
    const sounds = [];
    for (const raw of Array.isArray(value['sounds']) ? value['sounds'] : []) {
        const sound = soundOf(raw, animations);
        if (sound)
            sounds.push(sound);
    }
    const [length, height, width] = sides;
    return {
        slug,
        name,
        category,
        media,
        sizeAxis,
        realSize,
        box: { length: length, height: height, width: width },
        forwardYaw,
        triangles: triangles,
        drawCalls: drawCalls,
        gpuBytes: gpuBytes,
        cost,
        licence,
        credit,
        animations,
        sounds,
    };
}
const distance2 = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
/**
 * The calm path, step by step (contract C3): the one replay the scene state,
 * framing, the distance rule and the headset share. Each step's `models`
 * run in order -- walk, face, play -- and a model a step does not name
 * carries on as it was; the steps' `spannend` parts never change what the
 * next step starts from (PR-0 "Decided for you" 3). A walk to the pupil
 * (Spannend only) is not replayed here: model-distance.ts clamps it.
 */
export function replayModels(lesson, options = {}) {
    const doc = lesson;
    const objects = isRecord(doc['objects']) ? doc['objects'] : {};
    const steps = Array.isArray(doc['steps']) ? doc['steps'] : [];
    const catalogue = options.catalogue;
    const declared = new Map();
    const slugs = new Map();
    for (const [id, object] of Object.entries(objects)) {
        if (!isRecord(object) || typeof object['kind'] !== 'string')
            continue;
        const slug = modelSlugOf(object['kind']);
        const pos = object['pos'];
        if (slug === undefined || !isVec3(pos))
            continue;
        const turn = isFiniteNumber(object['turn']) ? object['turn'] : 0;
        declared.set(id, { pos: [pos[0], pos[1], pos[2]], turn, animation: MODEL_IDLE });
        slugs.set(id, slug);
    }
    const poses = new Map(declared);
    /** A circle's centre (ADR-054): a point, the stage centre, or an object's place as the step finds it. */
    const centreOf = (around) => {
        if (around === undefined)
            return [0, 0];
        if (isWalkPlace(around))
            return [around[0], around[1]];
        if (typeof around !== 'string')
            return undefined;
        const pose = poses.get(around);
        if (pose)
            return [pose.pos[0], pose.pos[2]];
        const object = objects[around];
        return isRecord(object) && isVec3(object['pos'])
            ? [object['pos'][0], object['pos'][2]]
            : undefined;
    };
    const replayed = [];
    const after = [];
    const last = Math.min(options.until ?? Infinity, steps.length - 1);
    for (let index = 0; index <= last; index++) {
        const step = steps[index];
        const moves = new Map();
        const list = !isRecord(step)
            ? undefined
            : options.spannendAt === index
                ? isRecord(step['spannend'])
                    ? step['spannend']['models']
                    : undefined
                : step['models'];
        for (const entry of Array.isArray(list) ? list : []) {
            if (!isRecord(entry) || typeof entry['object'] !== 'string')
                continue;
            const id = entry['object'];
            const start = poses.get(id);
            if (!start || moves.has(id))
                continue;
            const facts = catalogue?.[slugs.get(id)];
            const move = replayEntry(entry, id, start, facts, index, options, centreOf);
            moves.set(id, move);
            poses.set(id, move.end);
        }
        replayed.push(moves);
        after.push(new Map(poses));
    }
    return { declared, steps: replayed, after };
}
function replayEntry(entry, object, start, facts, index, options, centreOf) {
    const ring = circleOfEntry(entry, [start.pos[0], start.pos[2]], facts, centreOf);
    if (ring) {
        const y = start.pos[1];
        const turnIn = turnSeconds(start.turn, ring.heading);
        if (ring.rounds === 'loop') {
            return {
                object,
                start,
                end: {
                    pos: [ring.start[0], y, ring.start[1]],
                    turn: ring.heading,
                    animation: ring.gait,
                    circle: ring,
                },
                circle: ring,
                clips: [ring.gait],
                seconds: turnIn,
            };
        }
        const end = circleEnd(ring);
        const then = isAnimation(entry['then']) ? entry['then'] : MODEL_IDLE;
        const arc = ring.speed === undefined ? 0 : walkSeconds(circleLength(ring), ring.speed);
        return {
            object,
            start,
            end: { pos: [end.pos[0], y, end.pos[1]], turn: end.turn, animation: then },
            circle: ring,
            clips: then === ring.gait ? [ring.gait] : [ring.gait, then],
            seconds: turnIn + arc,
        };
    }
    let [x, , z] = start.pos;
    const y = start.pos[1];
    let turn = start.turn;
    let animation = start.animation;
    const clips = [];
    let walk;
    const walkTo = entry['walkTo'];
    if (isWalkPlace(walkTo)) {
        const from = [x, z];
        const to = [walkTo[0], walkTo[1]];
        const length = distance2(from, to);
        const heading = length > 1e-9 ? turnTowards(from, to) : turn;
        const gait = walkingGait(facts, entry['gait']);
        const speed = facts?.animations[gait]?.speed;
        const seconds = turnSeconds(turn, heading) +
            (speed !== undefined && speed > 0 ? walkSeconds(length, speed) : 0);
        walk = { from, to, gait, turnFrom: turn, heading, seconds };
        [x, z] = to;
        turn = heading;
        clips.push(gait);
    }
    // A turn in place (plan 4): never beside a walk (assessModels says so), where it stands.
    let turned;
    const heading = entry['turn'];
    if (!walk && isFiniteNumber(heading)) {
        const seconds = showTurnSeconds(turn, heading);
        if (seconds > 0)
            turned = { from: turn, to: heading, seconds };
        turn = heading;
    }
    let face;
    if (entry['face'] === 'pupil') {
        const target = options.pupil ? options.pupil(index) : [0, 0];
        if (target && distance2([x, z], target) > 1e-9) {
            const to = turnTowards([x, z], target);
            face = { from: turn, to, seconds: faceSeconds(turn, to) };
            turn = to;
        }
    }
    const then = isAnimation(entry['then']) ? entry['then'] : undefined;
    let playSeconds = 0;
    const play = entry['play'];
    if (isAnimation(play)) {
        const clip = facts?.animations[play];
        const loops = typeof entry['loop'] === 'boolean' ? entry['loop'] : (clip?.loop ?? play === MODEL_IDLE);
        clips.push(play);
        if (loops) {
            animation = play;
        }
        else {
            playSeconds = clip?.seconds ?? 0;
            animation = then ?? MODEL_IDLE;
        }
    }
    else if (walk) {
        animation = then ?? MODEL_IDLE;
    }
    else if (then) {
        animation = then;
    }
    if (!clips.includes(animation))
        clips.push(animation);
    return {
        object,
        start,
        end: { pos: [x, y, z], turn, animation },
        ...(walk ? { walk } : {}),
        ...(face ? { face } : {}),
        ...(turned ? { turned } : {}),
        clips,
        seconds: (walk?.seconds ?? 0) + (turned?.seconds ?? 0) + (face?.seconds ?? 0) + playSeconds,
    };
}
/**
 * The circle an entry makes (ADR-054): `circle` with a gait as `play` and no `walkTo`, round its
 * centre from where the step finds the model. Undefined otherwise, or with no centre or no radius:
 * the entry then only plays (`assessModels` and the distance rule say what is wrong).
 */
function circleOfEntry(entry, at, facts, centreOf) {
    const ring = entry['circle'];
    const play = entry['play'];
    if (!isRecord(ring) || entry['walkTo'] !== undefined || !isGait(play))
        return undefined;
    const centre = centreOf(ring['around']);
    const raw = ring['rounds'];
    const rounds = raw === 'loop' ? 'loop' : isFiniteNumber(raw) && raw > 0 ? raw : undefined;
    if (centre === undefined || rounds === undefined)
        return undefined;
    return circleOf({
        start: at,
        centre,
        direction: ring['direction'] === 'rechts' ? 'rechts' : 'links',
        rounds,
        gait: play,
        speed: facts?.animations[play]?.speed,
    });
}
/** Each model object's pose once steps 0..`index` ran (before the first: as declared). */
export function modelPosesAt(replay, index) {
    if (index < 0 || replay.after.length === 0)
        return replay.declared;
    return replay.after[Math.min(index, replay.after.length - 1)];
}
/**
 * What a model takes up while it plays `clips`, in its own frame at real
 * size (forward −z, on the ground): its box and everything those clips sweep
 * -- a bite reaches beyond the snout. The box alone without clips.
 */
export function modelSweptBox(facts, clips = []) {
    const { length, height, width } = facts.box;
    const min = [-width / 2, 0, -length / 2];
    const max = [width / 2, height, length / 2];
    for (const clip of clips) {
        const bounds = facts.animations[clip]?.bounds;
        if (!bounds)
            continue;
        for (let i = 0; i < 3; i++) {
            min[i] = Math.min(min[i], bounds.min[i]);
            max[i] = Math.max(max[i], bounds.max[i]);
        }
    }
    return { min: [min[0], min[1], min[2]], max: [max[0], max[1], max[2]] };
}
//# sourceMappingURL=models.js.map